import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';

import { CookieUtil } from '@utils/cookies';
import { LocalStorageUtil } from '@utils/localstorage';
import { environment } from '@enviroment/environment';
import { Globals } from '@config/global';

import { RegisterBonusService } from '@apiShared/user/register-bonus/register-bonus.service';
import { BonusService } from '@services/bonus/bonus.service';
import { RouletteService } from '@services/roulette/roulette.service';
import { CreateProcessUser } from '@class/process-user/create/create-process-user';
import { AlertConfirmComponent } from 'src/app/resources-alerts/alert-confirm/alert-confirm.component';

import { BonusResult } from '@services/bonus/interfaces/bonus.interface';
import { Subscription } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { CloseModal } from '../close-modal/close-modal';
import { NotificacionService } from '@utils/notificacion';
import { GlobalQueryParamsPreserverService } from '@apiShared/querys-params-preserver/querys-params-preserver.service';

@Injectable({
  providedIn: 'root',
})
export class BonusProcessHelper implements OnDestroy {
  private bonusResult: BonusResult | null = null;
  private bonusSubscription?: Subscription;
  private bonusID: number = 0;
  private env = environment;
  private closeModalClassName?: string;
  private errorCreateProcess: boolean = false;

  constructor(
    private cookies: CookieUtil,
    private localStorage: LocalStorageUtil,
    private router: Router,
    private globals: Globals,
    private api_register_bonus: RegisterBonusService,
    private bonusService: BonusService,
    private rouletteService: RouletteService,
    private create_process_user: CreateProcessUser,
    private closeModal: CloseModal,
    private modal: NgbModal,
    private notification: NotificacionService,
    private globalQueryParamsPreserver: GlobalQueryParamsPreserverService,
  ) {
    this.initializeBonusService();
    this.recoverExistingRouletteBonus();
  }

  async handleBonusProcess(info_obj_to_create_process: any, closeModalClassName: string): Promise<void> {
    const hasRouletteBonus = this.bonusResult && this.bonusResult.isValid;
    this.closeModalClassName = closeModalClassName;
    await this.registerBonus(Boolean(hasRouletteBonus), info_obj_to_create_process);
  }

  ngOnDestroy(): void {
    if (this.bonusSubscription) {
      this.bonusSubscription.unsubscribe();
    }
  }

  private initializeBonusService(): void {
    this.bonusSubscription = this.bonusService.getCurrentBonus().subscribe((bonusResult: BonusResult | null) => {
      this.bonusResult = bonusResult;
    });
  }

  private async recoverExistingRouletteBonus(): Promise<void> {
    try {
      await this.rouletteService.recoverExistingPrize();
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  private async registerBonus(force = false, info_obj_to_create_process: any): Promise<void> {
    let body: any = {};

    if (this.cookies.check('refcode')) {
      body = {
        bonus_name: 'REFERRED',
        conditions: {
          code: this.cookies.get('refcode'),
        },
      };
    } else {
      if (!this.bonusResult || !this.bonusResult.isValid) {
        this.clearRouletteState();
        this.onClickCreateProcess(info_obj_to_create_process);
        return Promise.resolve();
      }

      if (this.bonusResult) {
        const bonusName = this.bonusResult.tagNameBonusType || this.bonusResult.roulettePrize?.bonus_type;
        body.bonus_name = bonusName;
      }

      body.conditions = {
        amount: parseInt(info_obj_to_create_process.bank_amount),
        rate: parseFloat(info_obj_to_create_process.bank_rate),
        term: parseInt(info_obj_to_create_process.bank_term),
        bank_id: parseInt(info_obj_to_create_process.bank_id),
      };
    }

    return new Promise<void>((resolve) => {
      this.api_register_bonus.REGISTER_BONUS(body, this.cookies.get('id_token_user')).then((data: any) => {
        if (data.status === 201) {
          this.bonusID = data.body.id;
          this.errorCreateProcess = false;
          this.openAlertConfirmation({
            title: `
                <div class="d-block text-center">
                  <i class="mi mi-md mi-check-stars"></i>
                  <div class="d-block">
                    🎉 ¡Felicitaciones! 🎉
                  </div>
                </div>
              `,
            message: `
                <div class="text-center pb-2">
                  <p class="mb-3">
                   <strong>
                    ¡Estás a punto de activar tu inversión!
                   </strong>
                  </p>
                  <p class="mb-3">
                    Adicionalmente, tu premio ha sido registrado correctamente y lo recibirás en tu cuenta bancaria una vez tu CDT este activo
                  </p>
                </div>
              `,
            no_padding_header: false,
            textButtons: {
              confirm: {
                text: 'Continuar con mi CDT',
                id: 'continue_with_cdt_bonus_applied',
              },
            },
          }).then(() => {
            this.cookies.save('BonusAppliedCorrectly', 'true', 100);
            this.clearRouletteState();
            this.onClickCreateProcess(info_obj_to_create_process);
            resolve();
          });
        } else {
          if (data.error) {
            const errorMessages: { [key: string]: string } = {
              C001: 'ya tienes este enlace registrado en un proceso',
              C002: 'ya tienes un enlace de referido registrado',
              C003: 'el enlace que usaste no existe',
              C004: 'ya tienes CDTs activos',
              C005: 'ya has aplicado anteriormente',
              C006: 'no cumples con las condiciones',
              C007: 'ha ocurrido un error',
            };
            const messageError: string = data.error.errors
              ? errorMessages[data.error.errors.code] || data.error.errors.message
              : 'los beneficios no están disponibles en este momento debido a un problema interno';
            const codeError: string = data.error.errors ? data.error.errors.code : data.status;

            this.errorCreateProcess = true;
            this.openAlertConfirmation({
              title: `
                  <div class="d-block text-end text-size-sm text-black-100">
                    ERROR: ${codeError}
                  </div>
                  <div class="d-block text-center">
                    <i class="mi mi-md mi-percent-starts"></i>
                    <div class="d-block">
                      ¡Lo sentimos! No podemos validar tu beneficio
                    </div>
                  </div>
                `,
              message: `
                  Lamentamos informarte que no pudiste aplicar para el beneficio porque
                  ${messageError}.
                  <span class="text-blue-150 font-bold">
                    No obstante, puedes continuar con tu proceso de apertura sin ningún problema.
                  </span>
                  <div class="d-block text-center my-2">
                    <a href="https://api.whatsapp.com/send?phone=573160234714&text=Hola%2C%20estoy%20a%20punto%20de%20abrir%20un%20CDT%2C%20pero%20el%20bono%20no%20se%20registra" target="_blank" class="text-black-50">
                      Hablar con un asesor
                    </a
                  </div>
                `,
              no_padding_header: false,
              textButtons: {
                confirm: {
                  text: 'Aceptar',
                  id: 'select_accept_denial_referred_code',
                },
              },
            }).then(() => {
              this.onClickCreateProcess(info_obj_to_create_process);
              resolve();
            });
          }
        }
      });
    });
  }

  private onClickCreateProcess(info_obj_to_create_process: any): void {
    let body: any = {
      data: {
        bank_id: info_obj_to_create_process.bank_id,
        amount: parseInt(info_obj_to_create_process.bank_amount),
        term: info_obj_to_create_process.bank_term,
        rate: info_obj_to_create_process.bank_rate,
        phone: info_obj_to_create_process.phone,
        extra: info_obj_to_create_process.extra,
        environment_simulate: info_obj_to_create_process.environment_simulate,
      },
      bank_name: info_obj_to_create_process.bank_name,
      idToken: this.cookies.get('id_token_user'),
      modal: this.closeModalClassName,
    };

    if (this.bonusID != 0) {
      body.data['bonus_id'] = this.bonusID;
    }

    if (info_obj_to_create_process.turbo) {
      body.data['is_turbo_cdt'] = true;
    }

    this.addAdviserAgencyData(body.data);

    if (body.data.bank_id === this.globals.ID_FINANDINA) {
      this.closeModalTime();
      this.router.navigate(['/te-contactaremos'], { state: { body } });
    } else {
      this.create_process_user.CreateProcess_Helper(body).then((statusCode) => {
        switch (statusCode) {
          case 200:
            this.errorCreateProcess = false;
            this.bonusID = 0;
            this.clearRouletteState();
            // Desactivar la persistencia de UTM params y removerlos de la URL
            this.globalQueryParamsPreserver.stopPersistingAndRemoveUtmParams();
            break;

          case 400:
            this.showErrorMessage(
              'Tuvimos una pequeña interrupción de conexión. Intenta actualizar o prueba de nuevo en unos minutos.',
            );
            break;

          case 418:
            this.showErrorMessage(
              'Por ahora, KOA sólo permite un CDT por persona tanto en su plataforma como a través de MejorCDT, están trabajando para habilitar la opción de abrir varios CDT lo más pronto posible y te avisaremos en cuanto esté disponible.',
            );
            break;

          default:
            this.showErrorMessage(
              `Error ${statusCode}: La conexión tardó más de lo esperado. Vuelve a intentarlo y todo debería funcionar`,
            );
            break;
        }
      });
    }
  }

  private showErrorMessage(message: string): void {
    this.errorCreateProcess = true;
    this.openAlertConfirmation({
      title: `
        <div class="d-block text-center">
          <img src="${this.env.S3_STATIC_FILES}/images/web/warnig-bank-not-allied.svg" alt="Error" class="mb-2 bg-white w-75"/>
          <div class="d-block text-black">
            ¡Ups! Algo salió mal
          </div>
        </div>
      `,
      message: `
        <div class="text-center pb-2">
          <p class="mb-3 lh-normal">
            ${message}
          </p>
        </div>
      `,
      no_padding_header: false,
      textButtons: {
        confirm: {
          text: 'Entendido',
          id: 'accept_error_process',
        },
      },
    });
  }

  private addAdviserAgencyData(bodyData: any): void {
    if (this.env.BRAND !== 'coltefinanciera') {
      return;
    }

    try {
      if (this.cookies.check('adviser')) {
        const adviserValue = this.cookies.get('adviser');
        if (adviserValue) {
          bodyData.adviser = adviserValue;
          this.cookies.delete('adviser');
        }
      }

      if (this.cookies.check('agency')) {
        const agencyValue = this.cookies.get('agency');
        if (agencyValue) {
          bodyData.agency = agencyValue;
          this.cookies.delete('agency');
        }
      }
    } catch (error) {
      console.error('Error al procesar cookies de adviser/agency:', error);
    }
  }

  private clearRouletteState(): void {
    try {
      this.cookies.delete('ParticipationRuletteApplied');
      this.localStorage.delete('selectedPrizeRulette');
      this.localStorage.delete('pending_roulette_config');
      this.localStorage.delete('mcdt_stored_bonus_config');
      this.rouletteService.clearSavedPrize();
      this.bonusService.clearBonus();
    } catch (error) {
      console.error('Error', error);
    }
  }

  private openAlertConfirmation(modalData: {
    title: string;
    message: string;
    textButtons: {};
    no_padding_header: boolean;
  }): Promise<void> {
    return new Promise<void>((resolve) => {
      const modalRef = this.modal.open(AlertConfirmComponent, {
        centered: true,
        windowClass: 'modal-confirmation-all-process',
        backdrop: 'static',
      });
      modalRef.componentInstance.modalData = modalData;
      modalRef.componentInstance.getResult().subscribe((result: boolean) => {
        if (result) {
          if (!this.errorCreateProcess) {
            this.notification.sendNotificacion({
              api: 'api_open_loading_overlay_create_process',
            });
          }
          resolve();
        }
      });

      this.closeModalTime();
    });
  }

  closeModalTime() {
    setTimeout(() => {
      this.closeModal._CloseModal(this.closeModalClassName);
    }, 3000);
  }
}
